import { ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../../ui/components/ui/button';
import CourseCover from '../../../components/course-cover/course-cover';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5_000_000;

export interface CoverUploadFieldProps {
  /**
   * Existing image url to render when no new file picked (edit mode).
   */
  existingImageUrl?: string | null;
  /**
   * Called with the picked File or null when removed.
   */
  onChange: (file: File | null) => void;
  /**
   * Called when the user wants to remove the existing cover (edit mode only).
   * If omitted, the remove button only clears the picked file locally.
   */
  onRemoveExisting?: () => void;
  disabled?: boolean;
}

const CoverUploadField = ({ existingImageUrl, onChange, onRemoveExisting, disabled }: CoverUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pickedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pickedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedFile]);

  const handleFile = (file: File | null) => {
    setError(null);
    if (!file) {
      setPickedFile(null);
      onChange(null);
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Nur JPG, PNG oder WebP erlaubt.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Maximalgröße: 5 MB.');
      return;
    }
    setPickedFile(file);
    onChange(file);
  };

  const handleRemove = () => {
    if (pickedFile) {
      handleFile(null);
      return;
    }
    if (existingImageUrl && onRemoveExisting) {
      onRemoveExisting();
    }
  };

  const visibleUrl = previewUrl ?? existingImageUrl ?? null;

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        disabled={disabled}
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />

      {visibleUrl ? (
        // Shown through CourseCover, so the round crop here is exactly the one the course cards render.
        <div className="flex items-center gap-4 rounded-xl border border-[#E4E7EC] p-4">
          <CourseCover imageUrl={visibleUrl} size="lg" alt="Vorschau des Cover-Bilds" />
          <div className="flex flex-col gap-2 items-start">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
                Ersetzen
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleRemove} disabled={disabled}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="w-full h-28 rounded-xl border-2 border-dashed border-[#E4E7EC] hover:border-[#5FD5DE] hover:bg-[#F0F4FC] transition-colors flex flex-col items-center justify-center gap-2 text-[#888]">
          <ImagePlus className="w-7 h-7" />
          <span className="text-[14px]">Cover hochladen (max. 5 MB)</span>
          <span className="text-[12px]">Quadratisch, mittig zugeschnitten</span>
        </button>
      )}

      {error && <p className="text-[12px] text-red-600">{error}</p>}
    </div>
  );
};

export default CoverUploadField;
